import { Widget } from './widget';
import {MetaState} from "../api/StateResponse";
import {Observable, startWith, map} from "rxjs";
import {MatAutocompleteSelectedEvent} from "@angular/material/autocomplete";

export interface SelectWidgetOptionType {
	key: string;
	value: string;
	label: string;
	enabled: boolean;
}

export class SelectWidget extends Widget {
	type: string = 'select';
	multiple: boolean = false;
	objectLookup: boolean = false;
	lookupKey: string = 'id';
	options!: SelectWidgetOptionType[];
	selected: string[];

	constructor(options: {
		value?: string | null;
		name?: string;
		label?: string;
		required?: boolean;
		multiple?: boolean;
		objectLookup?: boolean;
		lookupKey?: string | null;
		options?: SelectWidgetOptionType[];
	} = {}) {
		super(options);

		this.selected = [];
		this.options = options.options || [];
		this.multiple = options.multiple || false;
		this.objectLookup = options.objectLookup || false;
		this.lookupKey = options.lookupKey || 'id';
		if (this.required && this.options.length > 0) {
			this.selected = [this.options[0].value];
		}
	}

	onChange(event: any) {
		if (this.blocked) {
			return;
		}

		if (this.node)
			this.node.onWidgetChange(event);
	}

	getValue(): any {
		if (this.multiple) {
			return this.selected;
		}

		if (!Array.isArray(this.selected)) {
			return this.selected;
		}

		if (this.selected.length === 0) {
			return null;
		}

		return this.selected[0];
	}

	protected unwrapLookup(value: any): any {
		if (this.objectLookup && value && typeof value === 'object' && this.lookupKey in value) {
			return value[this.lookupKey];
		}
		return value;
	}

	setValue(value: any) {
		value = this.unwrapLookup(value);

		if (Array.isArray(value)) {
			this.selected = value.map((entry: any) => this.unwrapLookup(entry));
		}
		else if (value !== null) {
			this.selected = value;
		}
		else {
			this.selected = [];
		}

		if (this.blocked) {
			return;
		}
		this.onChange(value);
	}

	setMetaState(meta: MetaState) {
		super.setMetaState(meta);

		for (let opt of this.options) {
			if (!(opt.key in meta.map)) {
				opt.enabled = true;
				continue;
			}
			opt.enabled = !!meta.map[opt.key];
		}
	}
}

export class RadioWidget extends SelectWidget {
	type: string = 'radio';

	constructor(options: {
		value?: string | null;
		name?: string;
		label?: string;
		required?: boolean;
		objectLookup?: boolean;
		lookupKey?: string | null;
		options?: SelectWidgetOptionType[];
	} = {}) {
		super({...options, ...{
			multiple: false,
		}});
	}

	getValue(): any {
		if (!this.value) {
			return null;
		}

		return this.value;
	}

	setValue(value: any) {
		value = this.unwrapLookup(value);
		this.value = value;
		this.onChange(value);
	}
}

export class SearchWidget extends SelectWidget {
	type: string = 'search';
	filteredOptions: Observable<SelectWidgetOptionType[]>;

	constructor(options: {
		value?: string | null;
		name?: string;
		label?: string;
		required?: boolean;
		objectLookup?: boolean;
		lookupKey?: string | null;
		options?: SelectWidgetOptionType[];
	} = {}) {
		super(options);
		this.filteredOptions = this.formControl.valueChanges.pipe(
			startWith(''),
			map(value => this._filter(value || '')),
		);
	}

	onChange(event: any) {
		if (this.blocked) {
			return;
		}

		if (event instanceof MatAutocompleteSelectedEvent) {
			this.value = event.option.value;
		}
		else {
			this.value = event;
		}

		if (this.node)
			this.node.onWidgetChange(event);
	}

	getValue(): any {
		if (!this.value) {
			return null;
		}

		return this.value;
	}

	setValue(value: any) {
		value = this.unwrapLookup(value);
		this.value = value;
		this.formControl.setValue(this.value);
		this.onChange(value);
	}

	setMetaState(meta: MetaState) {
		super.setMetaState(meta);

		// This is to make the autocomplete behave as the SelectWidget
		if (this.value in meta.map && !meta.map[this.value]) {
			this.setValue(null);
		}
	}

	displayFn = (value: string | null): string => {
		if (!value) return '';
		const opt = this.options.find(o => o.value === value);
		return opt ? opt.label : value;
	};

	private _filter(value: string): SelectWidgetOptionType[] {
		const filterValue = this._normalizeValue(value);
		return this.options.filter(opt => this._normalizeValue(opt.label).includes(filterValue));
	}

	private _normalizeValue(value: string): string {
		return value.toLowerCase().replace(/\s/g, '');
	}
}

export class GalleryWidget extends SelectWidget {
	type: string = 'gallery';
	thumbnails: Map<string, any> = new Map();

	constructor(options: {
		value?: string | null;
		name?: string;
		label?: string;
		required?: boolean;
		objectLookup?: boolean;
		lookupKey?: string | null;
		options?: SelectWidgetOptionType[];
		thumbnails?: Map<string, any>;
	} = {}) {
		super(options);
		if (options.thumbnails) {
			this.thumbnails = options.thumbnails;
		}
	}

	getThumbnailURL(opt: string) {
		const data = (<any>this.thumbnails)[opt];
		if (!data)
			return '';
		if (typeof data === 'string') {
			return `url("${data}");`;
		}
		return '';
	}
}
